/**
 * useCPMSuggestion Hook
 *
 * Smart CPM (Cost Per Mille) suggestion hook for social media valuations.
 * Provides platform-specific, country-aware, industry-adjusted CPM recommendations.
 *
 * Features:
 * - Auto-detects country from organization settings (language, timezone)
 * - Applies industry multipliers
 * - Shows confidence levels (high/medium/low)
 * - Supports historical CPM based on valuation_date
 * - Provides explanation text for tooltips
 * - Uses database-driven CPM benchmarks (dynamically updated through Settings)
 */

import { useMemo } from 'react';
import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { convertCurrency, getCurrencySymbol, CurrencyCode } from '@/lib/platform/utils/currency';
import { useExchangeRates } from '@/lib/platform/hooks/queries/use-exchange-rates';
import { useAllCPMBenchmarks, CPMBenchmark } from '@/lib/platform/hooks/queries/use-cpm-benchmarks';

export interface CPMSuggestionResult {
  // Core values
  suggestedCPM: number; // Converted to user's currency
  originalValue: number; // Original value from source (USD or EUR or DKK)
  originalCurrency: 'USD' | 'EUR' | 'DKK'; // Original currency from data source
  currency: string; // User's selected currency (EUR, USD, DKK, etc.)
  currencySymbol: string; // Currency symbol (€, $, kr, etc.)
  confidence: 'high' | 'medium' | 'low';

  // Context
  platform: string;
  metricType: 'followers' | 'impressions' | 'engagements';
  countryCode: string; // Any country code (DK, US, GB, ID, BR, AU, etc.) or GLOBAL
  countryName: string; // Human-readable country name
  industry?: string;

  // Explanation
  source: string;
  dataPeriod: string;
  explanation: string; // Human-readable explanation for tooltip

  // Historical context (if valuation_date provided)
  isHistorical: boolean;
  historicalNote?: string;
}

/**
 * Detect country from organization settings
 *
 * Priority:
 * 1. Organization country_code (from Settings page)
 * 2. Language = 'da' → Denmark
 * 3. Timezone mapping to specific countries
 * 4. Fallback to GLOBAL
 *
 * Returns actual country code (e.g., 'DK', 'US', 'ID', 'BR', 'AU', etc.)
 */
function detectCountry(countryCode?: string | null, language?: string, timezone?: string): string {
  // Organization country_code (highest priority)
  if (countryCode) {
    return countryCode.toUpperCase();
  }

  // Danish language → Denmark
  if (language === 'da') return 'DK';

  // Timezone-based country detection
  const tz = timezone || '';

  // Denmark
  if (tz.includes('Copenhagen') || tz.includes('Denmark')) return 'DK';

  // United States
  const usCities = ['New_York', 'Chicago', 'Los_Angeles', 'Denver', 'Phoenix', 'Anchorage'];
  if (usCities.some((city) => tz.includes(city))) return 'US';

  // United Kingdom
  if (tz.includes('London') || tz.includes('Dublin') || tz.includes('Edinburgh')) return 'GB';

  // Germany
  if (tz.includes('Berlin') || tz.includes('Munich') || tz.includes('Hamburg')) return 'DE';

  // France
  if (tz.includes('Paris')) return 'FR';

  // Norway
  if (tz.includes('Oslo') || tz.includes('Norway')) return 'NO';

  // Sweden
  if (tz.includes('Stockholm') || tz.includes('Sweden')) return 'SE';

  // Finland
  if (tz.includes('Helsinki') || tz.includes('Finland')) return 'FI';

  // Australia
  if (tz.includes('Sydney') || tz.includes('Melbourne') || tz.includes('Australia')) return 'AU';

  // Canada
  if (tz.includes('Toronto') || tz.includes('Vancouver') || tz.includes('Montreal')) return 'CA';

  // Fallback to GLOBAL for all other timezones
  return 'GLOBAL';
}

/**
 * Get country name from country code using centralized mapping
 */
function getCountryNameForCode(countryCode: string): string {
  if (countryCode === 'GLOBAL') return 'Global';

  // Import from centralized countries file
  // Note: Using dynamic import would be async, so we'll handle this inline
  // For now, we'll use a simplified mapping that covers common cases
  const countryNames: Record<string, string> = {
    DK: 'Denmark',
    US: 'United States',
    GB: 'United Kingdom',
    UK: 'United Kingdom', // Legacy support
    DE: 'Germany',
    FR: 'France',
    NO: 'Norway',
    SE: 'Sweden',
    FI: 'Finland',
    AU: 'Australia',
    CA: 'Canada',
    BR: 'Brazil',
    ID: 'Indonesia',
    PH: 'Philippines',
    CH: 'Switzerland',
    AT: 'Austria',
    IT: 'Italy',
    ES: 'Spain',
    NL: 'Netherlands',
    BE: 'Belgium',
    IN: 'India',
    SG: 'Singapore',
    NZ: 'New Zealand',
    JP: 'Japan',
    CN: 'China',
    MX: 'Mexico',
    TH: 'Thailand',
    GLOBAL: 'Global',
  };
  return countryNames[countryCode] || countryCode;
}

/**
 * Build human-readable explanation text
 */
function buildExplanation(benchmark: CPMBenchmark, countryCode: string, industry?: string): string {
  const parts: string[] = [];

  // Country context
  const countryName = getCountryNameForCode(countryCode);
  parts.push(`Based on ${countryName} market data`);

  // Industry context (if benchmark has industry-specific data)
  if (industry && benchmark.industry) {
    parts.push(`for ${industry} industry`);
  }

  // Source
  parts.push(`from ${benchmark.source}`);

  // Data period
  parts.push(`(${benchmark.data_period})`);

  return parts.join(', ');
}

/**
 * Check if valuation is historical (older than 6 months)
 */
function isHistoricalValuation(valuationDate?: string): boolean {
  if (!valuationDate) return false;

  const monthsAgo = (Date.now() - new Date(valuationDate).getTime()) / (1000 * 60 * 60 * 24 * 30);
  return monthsAgo >= 6;
}

/**
 * Get historical CPM note
 */
function getHistoricalNote(valuationDate: string): string {
  const valuationYear = new Date(valuationDate).getFullYear();
  const currentYear = new Date().getFullYear();

  if (valuationYear < currentYear) {
    return `This valuation is from ${valuationYear}. Current CPM rates may differ. Consider updating to ${currentYear} benchmarks for accuracy.`;
  }

  return `This valuation is older than 6 months. Consider reviewing CPM rates for current market conditions.`;
}

/**
 * useCPMSuggestion Hook
 *
 * @param platform - Platform ID (e.g., 'facebook', 'instagram', 'youtube')
 * @param metricType - Type of metric ('followers', 'impressions', 'engagements')
 * @param options - Optional configuration
 * @returns CPM suggestion with context and explanation
 *
 * @example
 * const suggestion = useCPMSuggestion('facebook', 'impressions', {
 *   industry: 'finance',
 *   valuationDate: '2024-06-15',
 *   currency: 'EUR'
 * });
 *
 * console.log(suggestion.suggestedCPM); // 26.74 ($28.49 USD converted to EUR)
 * console.log(suggestion.originalValue); // 28.49
 * console.log(suggestion.originalCurrency); // 'USD'
 * console.log(suggestion.currency); // 'EUR'
 * console.log(suggestion.currencySymbol); // '€'
 * console.log(suggestion.confidence); // 'high'
 * console.log(suggestion.explanation); // "Based on United States market data, adjusted for finance industry (1.5x), from SuperAds.ai (Dec 2024 - Dec 2025)"
 */
/**
 * Find CPM benchmark from database - EXACT MATCHES ONLY
 *
 * Single source of truth: Only returns data that exists in CPM Benchmarks database.
 * NO calculations, NO multipliers, NO assumptions.
 *
 * Fallback chain:
 * 1. Country + Platform + MetricType + Industry (exact match)
 * 2. Country + Platform + MetricType (general, no industry)
 * 3. GLOBAL + Platform + MetricType + Industry (exact match)
 * 4. GLOBAL + Platform + MetricType (general, no industry)
 * 5. null (if no exact match found in database)
 */
function findCPMBenchmark(
  benchmarks: CPMBenchmark[],
  platform: string,
  metricType: 'followers' | 'impressions' | 'engagements',
  countryCode: string,
  industry?: string
): CPMBenchmark | null {
  // 1. Try country + platform + metric + industry (exact match)
  if (industry) {
    const exactMatch = benchmarks.find(
      (b) =>
        b.platform === platform &&
        b.metric_type === metricType &&
        b.country_code === countryCode &&
        b.industry === industry &&
        b.active
    );
    if (exactMatch) return exactMatch;
  }

  // 2. Try country + platform + metric (general, no industry)
  // Only return if user didn't request specific industry data
  if (!industry) {
    const countryMatch = benchmarks.find(
      (b) =>
        b.platform === platform &&
        b.metric_type === metricType &&
        b.country_code === countryCode &&
        (!b.industry || b.industry === '') &&
        b.active
    );
    if (countryMatch) return countryMatch;
  }

  // 3. Fallback to GLOBAL + industry (exact match)
  if (industry) {
    const globalIndustryMatch = benchmarks.find(
      (b) =>
        b.platform === platform &&
        b.metric_type === metricType &&
        b.country_code === 'GLOBAL' &&
        b.industry === industry &&
        b.active
    );
    if (globalIndustryMatch) return globalIndustryMatch;
  }

  // 4. Fallback to GLOBAL (general, no industry)
  // Only return if user didn't request specific industry data
  if (!industry) {
    const globalMatch = benchmarks.find(
      (b) =>
        b.platform === platform &&
        b.metric_type === metricType &&
        b.country_code === 'GLOBAL' &&
        (!b.industry || b.industry === '') &&
        b.active
    );
    if (globalMatch) return globalMatch;
  }

  // 5. No exact match found - return null (single source of truth: database only)
  return null;
}

export function useCPMSuggestion(
  platform: string,
  metricType: 'followers' | 'impressions' | 'engagements',
  options?: {
    industry?: string;
    valuationDate?: string;
    currency?: string; // User's selected currency (EUR, USD, DKK, etc.)
  }
): CPMSuggestionResult | null {
  const { organization } = useOrganization();
  const { data: exchangeRatesData } = useExchangeRates();
  const { data: cpmData, isLoading } = useAllCPMBenchmarks();

  const result = useMemo(() => {
    // Detect country from organization settings
    const countryCode = detectCountry(
      organization?.country_code || undefined,
      organization?.default_language || undefined,
      organization?.default_timezone || undefined
    );

    // Get user's currency (from options or organization)
    const userCurrency = options?.currency || organization?.base_currency || 'DKK';

    // Get all benchmarks from database
    const allBenchmarks = cpmData?.benchmarks || [];

    // Find best matching benchmark with fallback logic
    const benchmark = findCPMBenchmark(
      allBenchmarks,
      platform,
      metricType,
      countryCode,
      options?.industry
    );

    // Single source of truth: If no benchmark found in database, return null
    if (!benchmark) {
      return null;
    }

    // Convert CPM from original currency (USD/EUR/DKK) to user's currency
    const originalValue = benchmark.original_value;
    const originalCurrency = benchmark.original_currency;

    const suggestedCPM = convertCurrency(
      originalValue,
      originalCurrency,
      userCurrency as CurrencyCode,
      exchangeRatesData?.rates
    );

    const currencySymbol = getCurrencySymbol(userCurrency);

    // Build explanation text
    const explanation = buildExplanation(benchmark, countryCode, options?.industry);

    // Check if historical
    const isHistorical = isHistoricalValuation(options?.valuationDate);
    const historicalNote = isHistorical ? getHistoricalNote(options!.valuationDate!) : undefined;

    return {
      suggestedCPM,
      originalValue,
      originalCurrency,
      currency: userCurrency,
      currencySymbol,
      confidence: benchmark.confidence,
      platform,
      metricType,
      countryCode,
      countryName: getCountryNameForCode(countryCode),
      industry: options?.industry,
      source: benchmark.source,
      dataPeriod: benchmark.data_period,
      explanation,
      isHistorical,
      historicalNote,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    platform,
    metricType,
    options?.industry,
    options?.valuationDate,
    options?.currency,
    organization?.country_code,
    organization?.default_language,
    organization?.default_timezone,
    organization?.base_currency,
    exchangeRatesData?.rates,
    cpmData?.benchmarks,
    isLoading,
  ]);

  return result;
}

/**
 * Get all CPM suggestions for a platform (all three metric types)
 *
 * Convenience hook for getting followers, impressions, and engagements CPM at once.
 *
 * @param platform - Platform ID
 * @param options - Optional configuration
 * @returns Object with three CPM suggestions
 *
 * @example
 * const suggestions = useAllCPMSuggestions('facebook', {
 *   industry: 'sports',
 *   currency: 'EUR'
 * });
 *
 * console.log(suggestions.followers.suggestedCPM); // 14.88 ($15.54 USD converted to EUR with live rates)
 * console.log(suggestions.impressions.suggestedCPM); // 20.78 ($22.20 USD converted to EUR)
 * console.log(suggestions.engagements.suggestedCPM); // 7.28 ($7.77 USD converted to EUR)
 */
export function useAllCPMSuggestions(
  platform: string,
  options?: {
    industry?: string;
    valuationDate?: string;
    currency?: string;
  }
) {
  const followers = useCPMSuggestion(platform, 'followers', options);
  const impressions = useCPMSuggestion(platform, 'impressions', options);
  const engagements = useCPMSuggestion(platform, 'engagements', options);

  return {
    followers,
    impressions,
    engagements,
  };
}
